import "./PrivacyPolicy.css";
import { Helmet } from "react-helmet-async";

const PrivacyPolicy = ({ dark }) => {
  return (
    <div className={`privacy-page ${dark ? "dark" : "light"}`}>
      <Helmet>
        <title>Privacy Policy | FadaSports - Data Protection & User Privacy</title>
        <meta name="description" content="Learn how FadaSports protects your personal data, privacy, and information. Read our comprehensive privacy policy and data handling practices." />
        <meta name="keywords" content="privacy policy, data protection, privacy protection, user privacy, terms of service" />
        <meta name="author" content="FadaSports" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Privacy Policy | FadaSports" />
        <meta property="og:description" content="FadaSports Privacy Policy - How we protect your data and privacy." />
        <meta property="og:image" content="https://fadasports.com/static/media/mobilelogo.d0257d49e02d3094cce0.png" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="FadaSports" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@FadaSports" />
      </Helmet>
      
      <section className="privacy-hero">
        <div className="privacy-overlay">
          <h1>Privacy Policy</h1>

          <p>
            FadaSports is committed to protecting user privacy, securing
            personal information, and maintaining transparency across our
            sports media, statistics, and analytics platform.
          </p>

          <span>Effective Date: May 13, 2026</span>
        </div>
      </section>

      <section className="privacy-container">

        <div className="privacy-card">
          <h2>1. Overview</h2>

          <p>
            This Privacy Policy explains how FadaSports (“FadaSports”, “we”,
            “our”, “us”) collects, uses, stores, shares, and protects
            personal data when you access or use our platforms, including
            our website, mobile applications, APIs, and related services
            (collectively, the “Services”).
          </p>

          <p>
            By using FadaSports, you agree to the practices described in
            this Policy.
          </p>
        </div>

        <div className="privacy-card">
          <h2>2. Scope of Services</h2>

          <p>
            FadaSports operates as a sports media, statistics, and analytics
            platform, providing:
          </p>

          <ul>
            <li>Sports data aggregation and visualization</li>
            <li>Match, player, and team statistics</li>
            <li>Editorial sports content and analysis</li>
            <li>Personalized sports dashboards and recommendations</li>
            <li>API-based sports data services (where applicable)</li>
          </ul>

          <p>
            This Policy applies across all digital products under
            FadaSports.
          </p>
        </div>

        <div className="privacy-card">
          <h2>3. Information We Collect</h2>

          <h3>3.1 Account Information</h3>

          <p>
            When you register or create an account, we may collect:
          </p>

          <ul>
            <li>Full name</li>
            <li>Email address</li>
            <li>Username and password (securely hashed)</li>
            <li>
              Profile preferences (favorite teams, leagues, players)
            </li>
          </ul>

          <h3>3.2 Usage & Interaction Data</h3>

          <p>
            We collect detailed interaction data to power analytics and
            personalization, including:
          </p>

          <ul>
            <li>
              Pages visited (e.g., match pages, player profiles, league
              dashboards)
            </li>
            <li>Teams, players, and leagues viewed or followed</li>
            <li>Time spent on statistical pages</li>
            <li>Clickstream behavior across the platform</li>
            <li>Search queries within the platform</li>
          </ul>

          <h3>3.3 Device & Technical Data</h3>

          <ul>
            <li>IP address</li>
            <li>Browser type and version</li>
            <li>Device type and operating system</li>
            <li>Approximate location (derived from IP, not precise GPS)</li>
            <li>Session identifiers and log data</li>
          </ul>

          <h3>3.4 Cookies & Tracking Technologies</h3>

          <p>
            We use cookies, SDKs, and similar technologies to:
          </p>

          <ul>
            <li>Maintain user sessions</li>
            <li>Store preferences</li>
            <li>Measure engagement and performance</li>
            <li>Improve recommendation systems</li>
          </ul>

          <p>
            Users may disable cookies, though some features may become
            limited.
          </p>

          <h3>3.5 Third-Party Sports Data</h3>

          <p>
            FadaSports integrates or aggregates data from third-party sports
            data providers, APIs, and public sources. This may include:
          </p>

          <ul>
            <li>Match results and live scores</li>
            <li>Player and team statistics</li>
            <li>Historical sports datasets</li>
          </ul>

          <p>
            We do not claim ownership of third-party raw sports data and may
            process it for analytical and presentation purposes.
          </p>
        </div>

        <div className="privacy-card">
          <h2>4. How We Use Data</h2>

          <h3>4.1 Core Platform Operations</h3>

          <ul>
            <li>Deliver sports statistics and content</li>
            <li>Maintain user accounts and dashboards</li>
            <li>Provide personalized sports experiences</li>
          </ul>

          <h3>4.2 Analytics & Intelligence</h3>

          <ul>
            <li>Build performance models and insights</li>
            <li>Analyze trends across teams, leagues, and players</li>
            <li>
              Improve ranking, recommendation, and discovery systems
            </li>
          </ul>

          <h3>4.3 Product Improvement</h3>

          <ul>
            <li>Debugging and system optimization</li>
            <li>Feature development and testing</li>
            <li>UX and engagement improvements</li>
          </ul>

          <h3>4.4 Communication</h3>

          <ul>
            <li>Service updates</li>
            <li>Security alerts</li>
            <li>Support responses</li>
          </ul>
        </div>

        <div className="privacy-card">
          <h2>5. Legal Basis for Processing (Where Applicable)</h2>

          <p>
            Where required under applicable laws (including NDPR and similar
            frameworks), we process personal data based on:
          </p>

          <ul>
            <li>User consent</li>
            <li>Contractual necessity (providing Services)</li>
            <li>
              Legitimate business interests (analytics, security,
              improvement)
            </li>
            <li>Legal obligations</li>
          </ul>
        </div>

        <div className="privacy-card">
          <h2>6. Data Sharing</h2>

          <p>We do not sell personal data.</p>

          <p>We may share limited data with:</p>

          <h3>6.1 Service Providers</h3>

          <ul>
            <li>Cloud hosting providers</li>
            <li>Analytics and monitoring tools</li>
            <li>Payment processors (if applicable)</li>
            <li>Infrastructure and security partners</li>
          </ul>

          <h3>6.2 Sports Data Partners</h3>

          <p>
            Third-party sports data providers used to enhance statistics and
            coverage.
          </p>

          <h3>6.3 Legal & Compliance</h3>

          <p>We may disclose data where required to:</p>

          <ul>
            <li>Comply with legal obligations</li>
            <li>Enforce terms of service</li>
            <li>Protect rights, users, or platform integrity</li>
          </ul>

          <p>
            All third parties are bound by confidentiality and data
            protection obligations.
          </p>
        </div>

        <div className="privacy-card">
          <h2>7. Data Retention</h2>

          <p>
            We retain data only as long as necessary for:
          </p>

          <ul>
            <li>Service delivery</li>
            <li>Platform analytics and improvement</li>
            <li>Legal and regulatory compliance</li>
            <li>Fraud prevention and security</li>
          </ul>

          <p>
            When no longer required, data is securely deleted or anonymized.
          </p>
        </div>

        <div className="privacy-card">
          <h2>8. Data Security</h2>

          <p>
            We implement industry-standard security measures, including:
          </p>

          <ul>
            <li>Encrypted data transmission (HTTPS/TLS)</li>
            <li>
              Secure password hashing and authentication protocols
            </li>
            <li>Access control and role-based restrictions</li>
            <li>Monitoring for suspicious activity</li>
            <li>Regular security reviews</li>
          </ul>

          <p>
            However, no system is completely immune to risk, and absolute
            security cannot be guaranteed.
          </p>
        </div>

        <div className="privacy-card">
          <h2>9. Data Subject Rights</h2>

          <p>
            Depending on your jurisdiction, you may have the right to:
          </p>

          <ul>
            <li>Access your personal data</li>
            <li>Request correction or updates</li>
            <li>Request deletion (“right to be forgotten”)</li>
            <li>Export your data in a portable format</li>
            <li>Withdraw consent where applicable</li>
            <li>Object to certain processing activities</li>
          </ul>

          <p>
            Requests can be submitted through our official support channels.
          </p>
        </div>

        <div className="privacy-card">
          <h2>10. Account Deletion</h2>

          <p>
            Users may request account deletion at any time.
          </p>

          <p>Upon deletion:</p>

          <ul>
            <li>Personal identifiers are removed or anonymized</li>
            <li>
              Certain data may be retained in aggregated or anonymized form
              for analytics
            </li>
            <li>
              Legal retention obligations may apply where required
            </li>
          </ul>
        </div>

        <div className="privacy-card">
          <h2>11. International Data Transfers</h2>

          <p>
            FadaSports may process or store data in servers located outside
            your country of residence. Where applicable, we ensure adequate
            safeguards for international data transfers in compliance with
            relevant data protection laws.
          </p>
        </div>

        <div className="privacy-card">
          <h2>12. Children’s Privacy</h2>

          <p>
            FadaSports is not intended for children under the age of 13 (or
            applicable digital consent age in your jurisdiction).
          </p>

          <p>
            We do not knowingly collect personal data from children without
            appropriate consent. If such data is identified, it will be
            promptly deleted.
          </p>
        </div>

        <div className="privacy-card">
          <h2>13. Automated Processing & Profiling</h2>

          <p>We use automated systems to:</p>

          <ul>
            <li>
              Generate sports insights and recommendations
            </li>
            <li>Rank content relevance</li>
            <li>Personalize dashboards and feeds</li>
          </ul>

          <p>
            These systems do not produce legal or similarly significant
            decisions affecting users.
          </p>
        </div>

        <div className="privacy-card">
          <h2>14. Limitation on Sports Data Accuracy</h2>

          <p>
            While we strive for accuracy, sports data may be subject to:
          </p>

          <ul>
            <li>Delays in third-party updates</li>
            <li>
              Corrections or revisions from official sources
            </li>
            <li>
              Temporary inconsistencies in live data feeds
            </li>
          </ul>

          <p>
            FadaSports does not guarantee absolute real-time accuracy of
            externally sourced sports information.
          </p>
        </div>

        <div className="privacy-card">
          <h2>15. Changes to This Policy</h2>

          <p>
            We may update this Privacy Policy periodically. Updates will be
            posted on this page with a revised effective date. Continued use
            of the Services constitutes acceptance of the updated Policy.
          </p>
        </div>

        <div className="privacy-card">
          <h2>16. Contact</h2>

          <p>
            For privacy-related inquiries, data requests, or concerns, users
            may contact our support team through official FadaSports
            communication channels.
          </p>
        </div>

        <div className="privacy-card closing-box">
          <h2>Closing Statement</h2>

          <p>
            FadaSports is committed to building a transparent, secure, and
            intelligent sports ecosystem where user trust and data
            protection are foundational principles.
          </p>
        </div>

      </section>

    </div>
  );
};

export default PrivacyPolicy;